/**
 * The `telegramChatID` the backend reports for a user who has no working
 * Telegram connection — a placeholder, not a real Telegram chat.
 */
const TELEGRAM_CHAT_ID_PLACEHOLDER = '0';

/** A numeric Telegram chat id — negative for groups/channels. */
const NUMERIC_TELEGRAM_CHAT_ID = /^-?\d+$/;

/**
 * Returns a user's `telegramChatID` only when it identifies a real Telegram
 * chat — i.e. the user has connected the Skynet chat bot. `null`, an empty
 * string and the `'0'` placeholder all mean "not connected" and yield
 * `undefined`.
 *
 * @param telegramChatID - A numeric chat id or an `@handle`, as returned by the users/profile APIs.
 */
export const getTelegramChatId = (telegramChatID: string | null | undefined): string | undefined =>
  telegramChatID && telegramChatID !== TELEGRAM_CHAT_ID_PLACEHOLDER ? telegramChatID : undefined;

/**
 * Returns a user's real Telegram chat id as a number, for APIs and Telegram
 * data that identify a chat numerically. Yields `undefined` when the user is
 * not connected (see {@link getTelegramChatId}) or the chat id is an
 * `@handle`, which has no numeric form.
 *
 * @param telegramChatID - A numeric chat id or an `@handle`, as returned by the users/profile APIs.
 */
export const getNumericTelegramChatId = (
  telegramChatID: string | null | undefined,
): number | undefined => {
  const chatId = getTelegramChatId(telegramChatID);
  return chatId !== undefined && NUMERIC_TELEGRAM_CHAT_ID.test(chatId) ? Number(chatId) : undefined;
};
